// WeddingPhotographyGallery.js - Wedding Photography Portfolio Gallery component
import { DEVICES } from '../../../constants/breakpoints';

/**
 * Creates a wedding photography portfolio gallery with image grid and lightbox
 * @param {function} getHierarchyName - Function to get hierarchy name for elements
 * @param {string} currentPageId - The current page ID where the gallery is being added
 * @param {function} setClassStylesRegistry - Function to update class styles registry
 * @param {function} setGlobalClassNames - Function to update global class names
 * @returns {Object} - The gallery elements
 */
export const createWeddingPhotographyGalleryElements = (
  getHierarchyName, 
  currentPageId,
  setClassStylesRegistry,
  setGlobalClassNames
) => {
  // Define class names
  const gallerySectionClass = 'wedding-gallery-section';
  const galleryContainerClass = 'wedding-gallery-container';
  const galleryHeaderClass = 'wedding-gallery-header';
  const galleryTitleClass = 'wedding-gallery-title';
  const gallerySubtitleClass = 'wedding-gallery-subtitle';
  const galleryGridClass = 'wedding-gallery-grid';
  const galleryImageItemClass = 'wedding-gallery-image-item';
  const galleryImageClass = 'wedding-gallery-image';
  const galleryImageOverlayClass = 'wedding-gallery-image-overlay';
  const galleryImageInfoClass = 'wedding-gallery-image-info';
  const galleryImageTitleClass = 'wedding-gallery-image-title';
  const galleryImageLocationClass = 'wedding-gallery-image-location';
  const galleryPatternOverlayClass = 'wedding-gallery-pattern-overlay';

  // Desktop Styles - Wedding theme with romantic design
  const gallerySectionStyles = {
    position: 'relative',
    paddingTop: '120px',
    paddingBottom: '120px',
    paddingLeft: '20px',
    paddingRight: '20px',
    backgroundColor: '#faf9f7',
    background: `
      radial-gradient(circle at 30% 20%, rgba(212, 175, 55, 0.05) 0%, transparent 50%),
      radial-gradient(circle at 70% 80%, rgba(255, 192, 203, 0.03) 0%, transparent 50%),
      linear-gradient(135deg, #faf9f7 0%, #f5f3f0 25%, #f0ede8 50%, #f5f3f0 75%, #faf9f7 100%)
    `,
    color: '#2d2d2d',
    width: '100%',
    overflow: 'hidden'
  };

  const galleryPatternOverlayStyles = {
    position: 'absolute',
    top: '0',
    left: '0',
    width: '100%',
    height: '100%',
    backgroundImage: `
      radial-gradient(circle at 25% 25%, rgba(212, 175, 55, 0.02) 1px, transparent 1px),
      radial-gradient(circle at 75% 75%, rgba(255, 192, 203, 0.02) 1px, transparent 1px)
    `,
    backgroundSize: '60px 60px, 100px 100px',
    backgroundPosition: '0 0, 50px 50px',
    zIndex: '0',
    opacity: '0.6'
  };

  const galleryContainerStyles = {
    maxWidth: '1400px',
    margin: '0 auto',
    position: 'relative',
    zIndex: '1'
  };

  const galleryHeaderStyles = {
    textAlign: 'center',
    marginBottom: '80px'
  };

  const galleryTitleStyles = {
    fontSize: '56px',
    fontWeight: '300',
    color: '#2d2d2d',
    marginBottom: '24px',
    fontFamily: '"Playfair Display", serif',
    letterSpacing: '2px',
    lineHeight: '1.1',
    position: 'relative'
  };

  const gallerySubtitleStyles = {
    fontSize: '20px',
    color: 'rgba(45, 45, 45, 0.8)',
    maxWidth: '700px',
    margin: '0 auto',
    lineHeight: '1.6',
    fontFamily: '"Inter", sans-serif',
    fontWeight: '300'
  };

  const galleryGridStyles = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
    gap: '30px',
    marginTop: '40px'
  };

  const galleryImageItemStyles = {
    position: 'relative',
    height: '400px',
    borderRadius: '8px',
    overflow: 'hidden',
    cursor: 'pointer',
    transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
    background: 'rgba(255, 255, 255, 0.9)',
    border: '1px solid rgba(212, 175, 55, 0.1)',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)'
  };

  const galleryImageItemHoverStyles = {
    transform: 'translateY(-8px) scale(1.02)',
    border: '1px solid rgba(212, 175, 55, 0.3)',
    boxShadow: `
      0 20px 40px rgba(0, 0, 0, 0.15),
      0 0 25px rgba(212, 175, 55, 0.2)
    `
  };

  const galleryImageStyles = {
    position: 'absolute',
    top: '0',
    left: '0',
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    transition: 'all 0.5s ease',
    filter: 'brightness(1) contrast(1.05)'
  };

  const galleryImageHoverStyles = {
    filter: 'brightness(0.8) contrast(1.1)',
    transform: 'scale(1.05)'
  };

  const galleryImageOverlayStyles = {
    position: 'absolute',
    top: '0',
    left: '0',
    width: '100%',
    height: '100%',
    background: `
      linear-gradient(135deg, 
        rgba(212, 175, 55, 0.1) 0%, 
        rgba(255, 192, 203, 0.1) 40%, 
        rgba(45, 45, 45, 0.6) 100%
      )
    `,
    opacity: '0',
    transition: 'all 0.5s ease',
    zIndex: '1'
  };

  const galleryImageOverlayHoverStyles = {
    opacity: '1'
  };

  const galleryImageInfoStyles = {
    position: 'absolute',
    bottom: '0',
    left: '0',
    right: '0',
    padding: '30px 25px',
    zIndex: '2',
    transform: 'translateY(20px)',
    opacity: '0',
    transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)'
  };

  const galleryImageInfoHoverStyles = {
    transform: 'translateY(0)',
    opacity: '1'
  };

  const galleryImageTitleStyles = {
    fontSize: '22px',
    fontWeight: '400',
    color: '#ffffff',
    marginBottom: '8px',
    fontFamily: '"Playfair Display", serif',
    letterSpacing: '1px',
    lineHeight: '1.2'
  };

  const galleryImageLocationStyles = {
    fontSize: '12px',
    color: '#d4af37',
    textTransform: 'uppercase',
    letterSpacing: '1px',
    fontFamily: '"Inter", sans-serif',
    fontWeight: '400'
  };

  // Tablet Styles
  const galleryTabletStyles = {
    paddingTop: '100px',
    paddingBottom: '100px',
    paddingLeft: '15px',
    paddingRight: '15px'
  };

  const galleryTitleTabletStyles = {
    fontSize: '48px'
  };

  const gallerySubtitleTabletStyles = {
    fontSize: '18px'
  };

  const galleryGridTabletStyles = {
    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
    gap: '25px'
  };

  const galleryImageItemTabletStyles = {
    height: '350px'
  };

  // Mobile Styles
  const galleryMobileStyles = {
    paddingTop: '80px',
    paddingBottom: '80px',
    paddingLeft: '15px',
    paddingRight: '15px'
  };

  const galleryTitleMobileStyles = {
    fontSize: '40px'
  };

  const gallerySubtitleMobileStyles = {
    fontSize: '16px'
  };

  const galleryGridMobileStyles = {
    gridTemplateColumns: '1fr',
    gap: '20px'
  };

  const galleryImageItemMobileStyles = {
    height: '300px'
  };

  const galleryImageInfoMobileStyles = {
    padding: '25px 20px'
  };

  const galleryImageTitleMobileStyles = {
    fontSize: '20px'
  };

  // Update the classStylesRegistry
  setClassStylesRegistry(prev => {
    const newRegistry = { ...prev };
    
    // Desktop styles
    newRegistry[DEVICES.DESKTOP].set(gallerySectionClass, gallerySectionStyles);
    newRegistry[DEVICES.DESKTOP].set(galleryPatternOverlayClass, galleryPatternOverlayStyles);
    newRegistry[DEVICES.DESKTOP].set(galleryContainerClass, galleryContainerStyles);
    newRegistry[DEVICES.DESKTOP].set(galleryHeaderClass, galleryHeaderStyles);
    newRegistry[DEVICES.DESKTOP].set(galleryTitleClass, galleryTitleStyles);
    newRegistry[DEVICES.DESKTOP].set(gallerySubtitleClass, gallerySubtitleStyles);
    newRegistry[DEVICES.DESKTOP].set(galleryGridClass, galleryGridStyles);
    newRegistry[DEVICES.DESKTOP].set(galleryImageItemClass, galleryImageItemStyles);
    newRegistry[DEVICES.DESKTOP].set(galleryImageClass, galleryImageStyles);
    newRegistry[DEVICES.DESKTOP].set(galleryImageOverlayClass, galleryImageOverlayStyles);
    newRegistry[DEVICES.DESKTOP].set(galleryImageInfoClass, galleryImageInfoStyles);
    newRegistry[DEVICES.DESKTOP].set(galleryImageTitleClass, galleryImageTitleStyles);
    newRegistry[DEVICES.DESKTOP].set(galleryImageLocationClass, galleryImageLocationStyles);

    // Hover styles
    newRegistry[DEVICES.DESKTOP].set(`${galleryImageItemClass}--hover`, galleryImageItemHoverStyles);
    newRegistry[DEVICES.DESKTOP].set(`${galleryImageClass}--hover`, galleryImageHoverStyles);
    newRegistry[DEVICES.DESKTOP].set(`${galleryImageOverlayClass}--hover`, galleryImageOverlayHoverStyles);
    newRegistry[DEVICES.DESKTOP].set(`${galleryImageInfoClass}--hover`, galleryImageInfoHoverStyles);

    // Tablet styles
    newRegistry[DEVICES.TABLET].set(gallerySectionClass, galleryTabletStyles);
    newRegistry[DEVICES.TABLET].set(galleryTitleClass, galleryTitleTabletStyles);
    newRegistry[DEVICES.TABLET].set(gallerySubtitleClass, gallerySubtitleTabletStyles);
    newRegistry[DEVICES.TABLET].set(galleryGridClass, galleryGridTabletStyles);
    newRegistry[DEVICES.TABLET].set(galleryImageItemClass, galleryImageItemTabletStyles);

    // Mobile styles
    newRegistry[DEVICES.MOBILE].set(gallerySectionClass, galleryMobileStyles);
    newRegistry[DEVICES.MOBILE].set(galleryTitleClass, galleryTitleMobileStyles);
    newRegistry[DEVICES.MOBILE].set(gallerySubtitleClass, gallerySubtitleMobileStyles);
    newRegistry[DEVICES.MOBILE].set(galleryGridClass, galleryGridMobileStyles);
    newRegistry[DEVICES.MOBILE].set(galleryImageItemClass, galleryImageItemMobileStyles);
    newRegistry[DEVICES.MOBILE].set(galleryImageInfoClass, galleryImageInfoMobileStyles);
    newRegistry[DEVICES.MOBILE].set(galleryImageTitleClass, galleryImageTitleMobileStyles);

    return newRegistry;
  });

  // Add classes to globalClassNames
  setGlobalClassNames(prev => new Set([
    ...prev,
    gallerySectionClass,
    galleryPatternOverlayClass,
    galleryContainerClass,
    galleryHeaderClass,
    galleryTitleClass,
    gallerySubtitleClass,
    galleryGridClass,
    galleryImageItemClass,
    galleryImageClass,
    galleryImageOverlayClass,
    galleryImageInfoClass,
    galleryImageTitleClass,
    galleryImageLocationClass
  ]));

  // Wedding photography images data
  const weddingImages = [
    {
      src: 'https://images.unsplash.com/photo-1519741497674-611481863552?w=600&h=400&fit=crop&crop=center',
      title: 'Romantic Garden Ceremony',
      location: 'Botanical Gardens'
    },
    {
      src: 'https://images.unsplash.com/photo-1465495976277-4387d4b0b4c6?w=600&h=400&fit=crop&crop=center',
      title: 'First Dance Magic',
      location: 'Grand Ballroom'
    },
    {
      src: 'https://images.unsplash.com/photo-1511285560929-80b456fea0bc?w=600&h=400&fit=crop&crop=center',
      title: 'Bridal Portrait Elegance',
      location: 'Historic Mansion'
    },
    {
      src: 'https://images.unsplash.com/photo-1529634597503-139d3726fed5?w=600&h=400&fit=crop&crop=center',
      title: 'Wedding Ring Details',
      location: 'Vintage Chapel'
    },
    {
      src: 'https://images.unsplash.com/photo-1606800052052-a08af7148866?w=600&h=400&fit=crop&crop=center',
      title: 'Couple\'s Joy Moment',
      location: 'Seaside Resort'
    },
    {
      src: 'https://images.unsplash.com/photo-1520854221256-17451cc331bf?w=600&h=400&fit=crop&crop=center',
      title: 'Reception Celebration',
      location: 'Country Club'
    },
    {
      src: 'https://images.unsplash.com/photo-1523438885200-e635ba2c371e?w=600&h=400&fit=crop&crop=center',
      title: 'Wedding Bouquet Beauty',
      location: 'Garden Pavilion'
    },
    {
      src: 'https://images.unsplash.com/photo-1525258946800-98cfd641d0de?w=600&h=400&fit=crop&crop=center',
      title: 'Groom\'s Preparation',
      location: 'Luxury Hotel'
    },
    {
      src: 'https://images.unsplash.com/photo-1607190074257-dd4b7af0309f?w=600&h=400&fit=crop&crop=center',
      title: 'Bride\'s Getting Ready',
      location: 'Bridal Suite'
    },
    {
      src: 'https://images.unsplash.com/photo-1522673607200-164d1b6ce486?w=600&h=400&fit=crop&crop=center',
      title: 'Wedding Party Fun',
      location: 'Vineyard Estate'
    },
    {
      src: 'https://images.unsplash.com/photo-1469371670807-013ccf25f16a?w=600&h=400&fit=crop&crop=center',
      title: 'Sunset Couple Portrait',
      location: 'Beach Resort'
    },
    {
      src: 'https://images.unsplash.com/photo-1515934751635-c81c6bc9a2d8?w=600&h=400&fit=crop&crop=center',
      title: 'Wedding Cake Cutting',
      location: 'Reception Hall'
    }
  ];

  // Generate IDs for elements
  const sectionId = Date.now();
  const patternOverlayId = Date.now() + 1;
  const containerId = Date.now() + 2;
  const headerId = Date.now() + 3;
  const titleId = Date.now() + 4;
  const subtitleId = Date.now() + 5;
  const gridId = Date.now() + 6;

  // Create image elements
  let currentId = gridId + 1;
  const imageElements = [];
  const imageIds = [];

  weddingImages.forEach((image, index) => {
    const itemId = currentId++;
    const imageId = currentId++;
    const overlayId = currentId++;
    const infoId = currentId++;
    const titleId = currentId++;
    const locationId = currentId++;

    imageIds.push(itemId);

    imageElements.push(
      // Image Item Container
      {
        id: itemId,
        builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type: 'div',
        name: `Wedding Image Item ${index + 1}`,
        parentId: gridId,
        hierarchyName: getHierarchyName('div'),
        className: galleryImageItemClass,
        styles: {},
        children: [imageId, overlayId, infoId],
        text: '',
        attributes: {
          'data-element': 'wedding-gallery-image-item',
          'data-index': index.toString()
        }
      },
      // Image
      {
        id: imageId,
        builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type: 'image',
        name: `Wedding Image ${index + 1}`,
        parentId: itemId,
        hierarchyName: getHierarchyName('image'),
        className: galleryImageClass,
        styles: {},
        src: image.src,
        attributes: {
          'data-element': 'wedding-gallery-image',
          'alt': image.title
        }
      },
      // Overlay
      {
        id: overlayId,
        builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type: 'div',
        name: `Wedding Image Overlay ${index + 1}`,
        parentId: itemId,
        hierarchyName: getHierarchyName('div'),
        className: galleryImageOverlayClass,
        styles: {},
        children: [],
        text: '',
        attributes: {
          'data-element': 'wedding-gallery-image-overlay'
        }
      },
      // Info
      {
        id: infoId,
        builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type: 'div',
        name: `Wedding Image Info ${index + 1}`,
        parentId: itemId,
        hierarchyName: getHierarchyName('div'),
        className: galleryImageInfoClass,
        styles: {},
        children: [titleId, locationId],
        text: '',
        attributes: {
          'data-element': 'wedding-gallery-image-info'
        }
      },
      // Title
      {
        id: titleId,
        builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type: 'h4',
        name: `Wedding Image Title ${index + 1}`,
        parentId: infoId,
        hierarchyName: getHierarchyName('h4'),
        className: galleryImageTitleClass,
        styles: {},
        children: [],
        text: image.title,
        attributes: {
          'data-element': 'wedding-gallery-image-title'
        }
      },
      // Location
      {
        id: locationId,
        builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type: 'span',
        name: `Wedding Image Location ${index + 1}`,
        parentId: infoId,
        hierarchyName: getHierarchyName('span'),
        className: galleryImageLocationClass,
        styles: {},
        children: [],
        text: image.location,
        attributes: {
          'data-element': 'wedding-gallery-image-location'
        }
      }
    );
  });

  // Create all elements
  const newElements = [
    // Main section
    {
      id: sectionId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'section',
      name: 'Wedding Photography Gallery Section',
      parentId: null,
      className: gallerySectionClass,
      styles: {},
      children: [patternOverlayId, containerId],
      text: '',
      attributes: {
        'data-element': 'wedding-gallery-section',
        'data-component': 'section',
        'data-section-type': 'wedding-gallery',
        'data-page-id': currentPageId
      }
    },
    // Pattern Overlay
    {
      id: patternOverlayId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Wedding Gallery Pattern Overlay',
      parentId: sectionId,
      hierarchyName: getHierarchyName('div'),
      className: galleryPatternOverlayClass,
      styles: {},
      children: [],
      text: '',
      attributes: {
        'data-element': 'wedding-gallery-pattern-overlay'
      }
    },
    // Container
    {
      id: containerId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Wedding Gallery Container',
      parentId: sectionId,
      hierarchyName: getHierarchyName('div'),
      className: galleryContainerClass,
      styles: {},
      children: [headerId, gridId],
      text: '',
      attributes: {
        'data-element': 'wedding-gallery-container'
      }
    },
    // Header
    {
      id: headerId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Wedding Gallery Header',
      parentId: containerId,
      hierarchyName: getHierarchyName('div'),
      className: galleryHeaderClass,
      styles: {},
      children: [titleId, subtitleId],
      text: '',
      attributes: {
        'data-element': 'wedding-gallery-header'
      }
    },
    // Title
    {
      id: titleId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'h2',
      name: 'Wedding Gallery Title',
      parentId: headerId,
      hierarchyName: getHierarchyName('h2'),
      className: galleryTitleClass,
      styles: {},
      children: [],
      text: 'Wedding Photography',
      attributes: {
        'data-element': 'wedding-gallery-title'
      }
    },
    // Subtitle
    {
      id: subtitleId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'p',
      name: 'Wedding Gallery Subtitle',
      parentId: headerId,
      hierarchyName: getHierarchyName('p'),
      className: gallerySubtitleClass,
      styles: {},
      children: [],
      text: 'Capturing the most precious moments of your special day with timeless elegance and romantic artistry. Each image tells the story of love, joy, and celebration.',
      attributes: {
        'data-element': 'wedding-gallery-subtitle'
      }
    },
    // Grid
    {
      id: gridId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Wedding Gallery Grid',
      parentId: containerId,
      hierarchyName: getHierarchyName('div'),
      className: galleryGridClass,
      styles: {},
      children: imageIds,
      text: '',
      attributes: {
        'data-element': 'wedding-gallery-grid'
      }
    },
    ...imageElements
  ];

  return {
    weddingGallerySection: newElements[0],
    elements: newElements
  };
}; 